import React from "react";
import styled from "styled-components";

type TextAlign = "left" | "center" | "right";
const Container = styled.div<{
    margin: string;
    textAlign?: TextAlign;
    fullWidth: boolean;
}>`
    position: relative;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};

    & input {
        border: 0;
        border-bottom: 1px solid #dcdbdc;
        width: ${(p) => (p.fullWidth ? "100%" : "auto")};
        outline: none;
        font-family: urdu, sans-serif;
        font-weight: 500;
        font-size: 18px;
        margin: ${(p) => p.margin};
        text-align: ${(p) => p.textAlign || "right"};

        &:focus {
            border-bottom-color: #000;
        }

        &[type="email"] {
            font-size: 18px;
            font-family: Roboto, sans-serif;
        }
    }

    input {
        background-color: transparent;
    }
`;

const ReadOnlyField = styled.div<{ fullWidth: boolean }>`
    user-select: none;
    border: 0;
    border-bottom: 1px solid #dcdbdc;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
    outline: none;
    font-family: Cairo, QuranFont, urdu, sans-serif;
    font-weight: 500;
    font-size: 18px;
    margin-top: 26px;
    line-height: 36px;
`;

const Placeholder = styled.div<{ focused: boolean }>`
    pointer-events: none;
    user-select: none;
    position: absolute;
    top: ${(p) => (p.focused ? 13 : 30)}px;
    transition: all 0.2s;
    color: ${(p) => (p.focused ? "#000" : "#dcdbdc")};
    font-size: ${(p) => (p.focused ? 12 : 16)}px;
    font-family: Cairo, QuranFont, urdu, sans-serif;
`;

const PlaceholderRight = styled(Placeholder)`
    right: 0;
`;

const PlaceholderLeft = styled(Placeholder)`
    width: 100%;
    text-align: left;
`;

const PlaceholderCenter = styled(Placeholder)`
    width: 100%;
    text-align: center;
`;

const FocusLineContainer = styled.div`
    pointer-events: none;
    user-select: none;
    position: absolute;
    bottom: 0px;
    height: 1px;
    width: 100%;
    background-color: transparent;
`;
const FocusLine = styled.div<{ focused: boolean }>`
    position: relative;
    margin: 0 auto;
    height: 1px;
    width: ${(p) => (p.focused ? 100 : 0)}%;
    background-color: ${(p) => (p.focused ? "#000" : "#dcdbdc")};
`;

interface InputProps {
    name: string;
    value: string;
    type?: string;
    placeholder: string;
    textAlign?: TextAlign;
    readOnly?: boolean;
    margin?: string;
    fullWidth: boolean;
    focusOnLoad?: boolean;

    onChange: (name: string, value: string) => void;
    onKeyUp?: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
}
interface InputState {
    focused: boolean;
}
export class Input extends React.Component<InputProps, InputState> {
    private field: React.RefObject<HTMLInputElement>;

    constructor(props: InputProps) {
        super(props);
        this.field = React.createRef();
        this.state = {
            focused: false,
        };
    }

    componentDidMount() {
        const c = this.field.current;
        if (c && !this.props.readOnly && this.props.focusOnLoad) {
            c.focus();
        }
    }

    focus = () => {
        const f = this.field;
        if (f && f.current) {
            f.current.focus();
        }
    };

    onBlur = () => this.setState({ focused: false });
    onFocus = () => this.setState({ focused: true });
    onChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.props.onChange(this.props.name, evt.target.value);

    placeholder() {
        const { focused } = this.state;
        const { value, placeholder, textAlign } = this.props;

        const f = focused || (!!value && value.length > 0);

        if (textAlign === "left") {
            return <PlaceholderLeft focused={f}>{placeholder}</PlaceholderLeft>;
        } else if (textAlign === "center") {
            return (
                <PlaceholderCenter focused={f}>{placeholder}</PlaceholderCenter>
            );
        }

        return <PlaceholderRight focused={f}>{placeholder}</PlaceholderRight>;
    }

    placeholderLine() {
        const { focused } = this.state;
        if (!focused) {
            return null;
        }

        return (
            <FocusLineContainer>
                <FocusLine focused={focused} />
            </FocusLineContainer>
        );
    }

    render() {
        const {
            value,
            type,
            margin,
            fullWidth,
            readOnly,
            textAlign,
        } = this.props;

        return (
            <Container
                margin={margin || "40px 0 0 0"}
                textAlign={textAlign || "right"}
                fullWidth={fullWidth}
            >
                {this.placeholder()}
                {this.placeholderLine()}
                {readOnly && (
                    <ReadOnlyField fullWidth={fullWidth}>{value}</ReadOnlyField>
                )}
                {!readOnly && (
                    <input
                        ref={this.field}
                        value={value || ""}
                        type={type || "text"}
                        autoComplete={
                            type === "password" ? "current-password" : "off"
                        }
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        onChange={this.onChange}
                        onKeyUp={this.props.onKeyUp}
                    />
                )}
            </Container>
        );
    }
}
